import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage, Language } from '@/contexts/LanguageContext';

interface SEOTranslations {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
}

interface PageSEO {
  [key: string]: SEOTranslations;
}

// Structured data translations
interface StructuredDataTranslations {
  siteName: string;
  siteDescription: string;
  homePageName: string;
  schedulePageName: string;
  draftPageName: string;
  olympicsPageName: string;
  breadcrumbHome: string;
  breadcrumbSchedule: string;
  breadcrumbDraft: string;
  breadcrumbOlympics: string;
}

const structuredDataI18n: Record<Language, StructuredDataTranslations> = {
  en: {
    siteName: "Ice Ace Hub",
    siteDescription: "Your ultimate hockey destination for live NHL, KHL, SHL scores, player stats, playoff brackets, betting odds, and comprehensive league standings.",
    homePageName: "Home - Live Hockey Stats, Scores & Betting Odds",
    schedulePageName: "Hockey Game Schedule",
    draftPageName: "NHL Draft Rankings",
    olympicsPageName: "Milano Cortina 2026 Olympics Ice Hockey",
    breadcrumbHome: "Home",
    breadcrumbSchedule: "Schedule",
    breadcrumbDraft: "Draft Rankings",
    breadcrumbOlympics: "Olympics 2026",
  },
  sv: {
    siteName: "Ice Ace Hub",
    siteDescription: "Din ultimata hockeydestination för live NHL, KHL, SHL resultat, spelarstatistik, slutspelsträd, odds och omfattande ligatabeller.",
    homePageName: "Hem - Live Hockeystatistik, Resultat & Odds",
    schedulePageName: "Hockeyschema",
    draftPageName: "NHL Draft Ranking",
    olympicsPageName: "Milano Cortina 2026 OS Ishockey",
    breadcrumbHome: "Hem",
    breadcrumbSchedule: "Schema",
    breadcrumbDraft: "Draft Ranking",
    breadcrumbOlympics: "OS 2026",
  },
  fi: {
    siteName: "Ice Ace Hub",
    siteDescription: "Täydellinen jääkiekkokohteesi live NHL, KHL, SHL tuloksille, pelaajastatistiikalle, playoff-kaavioille, kertoimille ja kattaville sarjataulukoille.",
    homePageName: "Koti - Live Jääkiekkotilastot, Tulokset & Kertoimet",
    schedulePageName: "Jääkiekkoaikataulu",
    draftPageName: "NHL Draft-sijoitukset",
    olympicsPageName: "Milano Cortina 2026 Olympialaiset Jääkiekko",
    breadcrumbHome: "Koti",
    breadcrumbSchedule: "Aikataulu",
    breadcrumbDraft: "Draft-sijoitukset",
    breadcrumbOlympics: "Olympialaiset 2026",
  },
  ru: {
    siteName: "Ice Ace Hub",
    siteDescription: "Ваш лучший источник live результатов НХЛ, КХЛ (Континентальная хоккейная лига), SHL, статистики игроков, турнирных сеток плей-офф, коэффициентов и таблиц лиг.",
    homePageName: "Главная - Хоккейная Статистика НХЛ и КХЛ, Результаты и Коэффициенты",
    schedulePageName: "Расписание Матчей НХЛ и КХЛ",
    draftPageName: "Рейтинг Драфта НХЛ",
    olympicsPageName: "Милано Кортина 2026 Олимпийский Хоккей",
    breadcrumbHome: "Главная",
    breadcrumbSchedule: "Расписание",
    breadcrumbDraft: "Рейтинг Драфта",
    breadcrumbOlympics: "Олимпиада 2026",
  },
  de: {
    siteName: "Ice Ace Hub",
    siteDescription: "Ihre ultimative Eishockey-Destination für Live NHL, KHL, DEL Ergebnisse, Spielerstatistiken, Playoff-Übersichten, Wettquoten und Tabellen.",
    homePageName: "Startseite - Live Eishockey-Statistiken, Ergebnisse & Quoten",
    schedulePageName: "Eishockey-Spielplan",
    draftPageName: "NHL Draft-Rangliste",
    olympicsPageName: "Milano Cortina 2026 Olympia Eishockey",
    breadcrumbHome: "Startseite",
    breadcrumbSchedule: "Spielplan",
    breadcrumbDraft: "Draft-Rangliste",
    breadcrumbOlympics: "Olympia 2026",
  },
  cs: {
    siteName: "Ice Ace Hub",
    siteDescription: "Váš nejlepší zdroj pro živé výsledky NHL, KHL, Extraligy, statistiky hráčů, pavouk play-off, sázkové kurzy a tabulky lig.",
    homePageName: "Domů - Živé Hokejové Statistiky, Výsledky & Kurzy",
    schedulePageName: "Hokejový Rozvrh",
    draftPageName: "NHL Draft Žebříček",
    olympicsPageName: "Milano Cortina 2026 Olympijský Hokej",
    breadcrumbHome: "Domů",
    breadcrumbSchedule: "Rozvrh",
    breadcrumbDraft: "Draft Žebříček",
    breadcrumbOlympics: "Olympiáda 2026",
  },
  fr: {
    siteName: "Ice Ace Hub",
    siteDescription: "Votre destination ultime pour les scores en direct de la LNH, KHL, SHL, statistiques des joueurs, tableaux des séries, cotes de paris et classements des ligues.",
    homePageName: "Accueil - Statistiques Hockey En Direct, Scores & Cotes",
    schedulePageName: "Calendrier Des Matchs",
    draftPageName: "Classement Du Repêchage LNH",
    olympicsPageName: "Hockey Sur Glace Milano Cortina 2026",
    breadcrumbHome: "Accueil",
    breadcrumbSchedule: "Calendrier",
    breadcrumbDraft: "Classement Repêchage",
    breadcrumbOlympics: "Olympiques 2026",
  },
};

const pageSEO: Record<Language, PageSEO> = {
  en: {
    '/': {
      title: "Ice Ace Hub | Live Hockey Stats, Scores & Betting Odds",
      description: "Your ultimate hockey destination for live NHL, KHL, SHL scores, player stats, playoff brackets, betting odds, and comprehensive league standings.",
      ogTitle: "Ice Ace Hub | Live Hockey Stats & Scores",
      ogDescription: "Live hockey scores, player stats, playoff brackets, and betting odds for NHL, KHL, SHL and more leagues worldwide.",
    },
    '/schedule': {
      title: "Game Schedule | Ice Ace Hub",
      description: "Complete hockey game schedule for NHL, KHL, SHL and more leagues. Check upcoming games, times, and matchups.",
      ogTitle: "Hockey Game Schedule | Ice Ace Hub",
      ogDescription: "Check the complete hockey schedule for all major leagues including NHL, KHL, and SHL.",
    },
    '/draft-rankings': {
      title: "NHL Draft Rankings | Ice Ace Hub",
      description: "Top NHL draft prospects and rankings. Discover future hockey stars and their scouting reports.",
      ogTitle: "NHL Draft Rankings | Ice Ace Hub",
      ogDescription: "Complete NHL draft rankings and top prospect analysis.",
    },
    '/olympics-2026': {
      title: "Milano Cortina 2026 Olympics Hockey | IceHub",
      description: "Complete 2026 Winter Olympics ice hockey schedule, venues, and broadcast info. Men's and Women's tournament games from February 6-22, 2026.",
      ogTitle: "2026 Olympics Ice Hockey | IceHub",
      ogDescription: "Full Milano Cortina 2026 Olympics ice hockey schedule with game times, venues, and how to watch.",
    },
    '/dashboard': {
      title: "Your Hockey Dashboard | IceHub",
      description: "Your personalized hockey hub with favorite teams, players, alerts, live scores and upcoming events tailored to you.",
      ogTitle: "Your Hockey Dashboard | IceHub",
      ogDescription: "Personalized hockey dashboard with your favorite teams, players, scores and alerts.",
    },
    '/pricing': {
      title: "Pricing & Plans | IceHub",
      description: "Compare IceHub Free, Light, Pro and Elite plans. Live odds, advanced stats, priority alerts and more — with a free trial on paid tiers.",
      ogTitle: "IceHub Pricing & Plans",
      ogDescription: "Compare Free, Light, Pro and Elite plans. Live odds, alerts and advanced stats with a free trial.",
    },
    '/account': {
      title: "Your Account | IceHub",
      description: "Manage your IceHub account, subscription, alerts, devices and personalization settings in one place.",
      ogTitle: "Your IceHub Account",
      ogDescription: "Manage your account, subscription, alerts and personalization settings.",
    },
    '/admin': {
      title: "Admin Console | IceHub",
      description: "IceHub admin console for managing users, subscriptions and platform settings.",
      ogTitle: "IceHub Admin Console",
      ogDescription: "Manage users, subscriptions and platform settings.",
    },
    '/unsubscribe': {
      title: "Unsubscribe from Alerts | IceHub",
      description: "Manage or unsubscribe from IceHub email alerts for your favorite teams, players and tournaments.",
      ogTitle: "Unsubscribe from IceHub Alerts",
      ogDescription: "Manage or unsubscribe from IceHub email alerts.",
    },
    '/khl-playoffs': {
      title: "KHL Playoff Bracket & Gagarin Cup | IceHub",
      description: "Live KHL playoff bracket, Gagarin Cup matchups, series scores and projections for the Kontinental Hockey League postseason.",
      ogTitle: "KHL Playoff Bracket & Gagarin Cup | IceHub",
      ogDescription: "Live KHL playoff bracket, Gagarin Cup matchups, series scores and projections.",
    },
    '/auth': {
      title: "Sign In or Create Account | IceHub",
      description: "Sign in or create a free IceHub account to personalize your hockey experience with favorite teams, players and alerts.",
      ogTitle: "Sign In to IceHub",
      ogDescription: "Sign in or create a free account to personalize your hockey experience.",
    },
    '/privacy': {
      title: "Privacy Policy | IceHub",
      description: "Read the IceHub privacy policy explaining what data we collect, how we use it and your rights under GDPR.",
      ogTitle: "IceHub Privacy Policy",
      ogDescription: "How IceHub collects, uses and protects your data.",
    },
  },
  sv: {
    '/': {
      title: "Ice Ace Hub | Live Hockeystatistik, Resultat & Odds",
      description: "Din ultimata hockeydestination för live NHL, KHL, SHL resultat, spelarstatistik, slutspelsträd, odds och omfattande ligatabeller.",
      ogTitle: "Ice Ace Hub | Live Hockeystatistik & Resultat",
      ogDescription: "Live hockeyresultat, spelarstatistik, slutspelsträd och odds för NHL, KHL, SHL och fler ligor världen över.",
    },
    '/schedule': {
      title: "Spelschema | Ice Ace Hub",
      description: "Komplett hockeyschema för NHL, KHL, SHL och fler ligor. Se kommande matcher, tider och uppställningar.",
      ogTitle: "Hockeyschema | Ice Ace Hub",
      ogDescription: "Se det kompletta hockeyschemat för alla stora ligor inklusive NHL, KHL och SHL.",
    },
    '/draft-rankings': {
      title: "NHL Draft Ranking | Ice Ace Hub",
      description: "Topp NHL draft-prospekt och rankingar. Upptäck framtida hockeystjärnor och deras scoutrapporter.",
      ogTitle: "NHL Draft Ranking | Ice Ace Hub",
      ogDescription: "Komplett NHL draft-ranking och toppprospektanalys.",
    },
    '/olympics-2026': {
      title: "Milano Cortina 2026 OS Ishockey Schema | Ice Ace Hub",
      description: "Komplett OS 2026 ishockeyschema, arenor och sändningsinformation. Dam- och herrturnering 6-22 februari 2026.",
      ogTitle: "OS 2026 Ishockey | Ice Ace Hub",
      ogDescription: "Fullständigt Milano Cortina 2026 OS ishockeyschema med tider, arenor och hur du ser matcherna.",
    },
  },
  fi: {
    '/': {
      title: "Ice Ace Hub | Live Jääkiekkotilastot, Tulokset & Kertoimet",
      description: "Täydellinen jääkiekkokohteesi live NHL, KHL, SHL tuloksille, pelaajastatistiikalle, playoff-kaavioille, kertoimille ja kattaville sarjataulukoille.",
      ogTitle: "Ice Ace Hub | Live Jääkiekkotilastot & Tulokset",
      ogDescription: "Live jääkiekkotulokset, pelaajastatistiikat, playoff-kaaviot ja kertoimet NHL:lle, KHL:lle, SHL:lle ja muille sarjoille.",
    },
    '/schedule': {
      title: "Peliaikataulu | Ice Ace Hub",
      description: "Täydellinen jääkiekkoaikataulu NHL:lle, KHL:lle, SHL:lle ja muille sarjoille. Tarkista tulevat pelit ja ajat.",
      ogTitle: "Jääkiekkoaikataulu | Ice Ace Hub",
      ogDescription: "Tarkista täydellinen jääkiekkoaikataulu kaikille suurille sarjoille.",
    },
    '/draft-rankings': {
      title: "NHL Draft-sijoitukset | Ice Ace Hub",
      description: "NHL:n parhaiden draft-lupausten ranking. Löydä tulevaisuuden jääkiekkotähdet.",
      ogTitle: "NHL Draft-sijoitukset | Ice Ace Hub",
      ogDescription: "Täydellinen NHL draft-ranking ja huippulupausten analyysi.",
    },
    '/olympics-2026': {
      title: "Milano Cortina 2026 Olympialaiset Jääkiekko | Ice Ace Hub",
      description: "Täydellinen 2026 talviolympialaisten jääkiekkoaikataulu, areenat ja lähetystiedot. Miesten ja naisten turnaukset 6.-22. helmikuuta 2026.",
      ogTitle: "Olympialaiset 2026 Jääkiekko | Ice Ace Hub",
      ogDescription: "Milano Cortina 2026 olympialaisten jääkiekkoaikataulu peliajoilla, areenoilla ja katseluohjeilla.",
    },
  },
  ru: {
    '/': {
      title: "Ice Ace Hub | НХЛ и КХЛ Статистика, Результаты и Коэффициенты",
      description: "Ваш лучший источник live результатов НХЛ, КХЛ (Континентальная хоккейная лига), SHL, статистики игроков, турнирных сеток плей-офф, коэффициентов и таблиц лиг.",
      ogTitle: "Ice Ace Hub | НХЛ и КХЛ Хоккейная Статистика и Результаты",
      ogDescription: "Live хоккейные результаты, статистика игроков, сетки плей-офф и коэффициенты для НХЛ, КХЛ (Континентальная хоккейная лига), SHL и других лиг.",
    },
    '/schedule': {
      title: "Расписание Матчей НХЛ и КХЛ | Ice Ace Hub",
      description: "Полное расписание хоккейных матчей НХЛ, КХЛ, SHL и других лиг. Проверьте предстоящие игры и время.",
      ogTitle: "Расписание Матчей НХЛ и КХЛ | Ice Ace Hub",
      ogDescription: "Полное расписание хоккейных матчей для всех крупных лиг включая КХЛ.",
    },
    '/draft-rankings': {
      title: "Рейтинг Драфта НХЛ | Ice Ace Hub",
      description: "Топ проспектов и рейтинги драфта НХЛ. Откройте будущих звезд хоккея включая игроков КХЛ.",
      ogTitle: "Рейтинг Драфта НХЛ | Ice Ace Hub",
      ogDescription: "Полный рейтинг драфта НХЛ и анализ топ-проспектов включая игроков КХЛ.",
    },
    '/olympics-2026': {
      title: "Милано Кортина 2026 Олимпийский Хоккей | Ice Ace Hub",
      description: "Полное расписание хоккея на зимних Олимпийских играх 2026, арены и трансляции. Мужской и женский турниры 6-22 февраля 2026.",
      ogTitle: "Олимпиада 2026 Хоккей | Ice Ace Hub",
      ogDescription: "Расписание хоккея на Олимпиаде Милано Кортина 2026 с временем матчей и информацией о трансляциях.",
    },
  },
  de: {
    '/': {
      title: "Ice Ace Hub | Live Eishockey-Statistiken, Ergebnisse & Quoten",
      description: "Ihre ultimative Eishockey-Destination für Live NHL, KHL, DEL Ergebnisse, Spielerstatistiken, Playoff-Übersichten, Wettquoten und Tabellen.",
      ogTitle: "Ice Ace Hub | Live Eishockey-Statistiken & Ergebnisse",
      ogDescription: "Live Eishockey-Ergebnisse, Spielerstatistiken, Playoff-Übersichten und Wettquoten für NHL, KHL, DEL und mehr.",
    },
    '/schedule': {
      title: "Spielplan | Ice Ace Hub",
      description: "Vollständiger Eishockey-Spielplan für NHL, KHL, DEL und weitere Ligen. Prüfen Sie kommende Spiele und Zeiten.",
      ogTitle: "Eishockey-Spielplan | Ice Ace Hub",
      ogDescription: "Der vollständige Eishockey-Spielplan für alle großen Ligen.",
    },
    '/draft-rankings': {
      title: "NHL Draft-Rangliste | Ice Ace Hub",
      description: "Top NHL Draft-Prospects und Rankings. Entdecken Sie zukünftige Eishockey-Stars.",
      ogTitle: "NHL Draft-Rangliste | Ice Ace Hub",
      ogDescription: "Vollständige NHL Draft-Rangliste und Top-Prospect-Analyse.",
    },
    '/olympics-2026': {
      title: "Milano Cortina 2026 Olympia Eishockey | Ice Ace Hub",
      description: "Vollständiger Eishockey-Spielplan der Winterolympiade 2026, Arenen und Übertragungen. Damen- und Herrenturniere vom 6.-22. Februar 2026.",
      ogTitle: "Olympia 2026 Eishockey | Ice Ace Hub",
      ogDescription: "Milano Cortina 2026 Olympia Eishockey-Spielplan mit Spielzeiten, Arenen und Übertragungsinfos.",
    },
  },
  cs: {
    '/': {
      title: "Ice Ace Hub | Živé Hokejové Statistiky, Výsledky & Kurzy",
      description: "Váš nejlepší zdroj pro živé výsledky NHL, KHL, Extraligy, statistiky hráčů, pavouk play-off, sázkové kurzy a tabulky lig.",
      ogTitle: "Ice Ace Hub | Živé Hokejové Statistiky & Výsledky",
      ogDescription: "Živé hokejové výsledky, statistiky hráčů, pavouk play-off a kurzy pro NHL, KHL, Extraligu a další ligy.",
    },
    '/schedule': {
      title: "Rozvrh Zápasů | Ice Ace Hub",
      description: "Kompletní hokejový rozvrh pro NHL, KHL, Extraligu a další ligy. Zkontrolujte nadcházející zápasy a časy.",
      ogTitle: "Hokejový Rozvrh | Ice Ace Hub",
      ogDescription: "Kompletní hokejový rozvrh pro všechny velké ligy.",
    },
    '/draft-rankings': {
      title: "NHL Draft Žebříček | Ice Ace Hub",
      description: "Top NHL draft prospekty a žebříčky. Objevte budoucí hokejové hvězdy.",
      ogTitle: "NHL Draft Žebříček | Ice Ace Hub",
      ogDescription: "Kompletní NHL draft žebříček a analýza top prospektů.",
    },
    '/olympics-2026': {
      title: "Milano Cortina 2026 Olympijský Hokej | Ice Ace Hub",
      description: "Kompletní program hokeje na zimních olympijských hrách 2026, arény a vysílání. Mužský a ženský turnaj 6.-22. února 2026.",
      ogTitle: "Olympiáda 2026 Hokej | Ice Ace Hub",
      ogDescription: "Milano Cortina 2026 olympijský hokejový program s časy zápasů, arénami a informacemi o vysílání.",
    },
  },
  fr: {
    '/': {
      title: "Ice Ace Hub | Statistiques Hockey En Direct, Scores & Cotes",
      description: "Votre destination ultime pour les scores en direct de la LNH, KHL, SHL, statistiques des joueurs, tableaux des séries, cotes de paris et classements des ligues.",
      ogTitle: "Ice Ace Hub | Statistiques Hockey En Direct & Scores",
      ogDescription: "Scores de hockey en direct, statistiques des joueurs, tableaux des séries et cotes de paris pour la LNH, KHL, SHL et plus.",
    },
    '/schedule': {
      title: "Calendrier Des Matchs | Ice Ace Hub",
      description: "Calendrier complet des matchs de hockey pour la LNH, KHL, SHL et plus. Consultez les matchs à venir et les horaires.",
      ogTitle: "Calendrier Hockey | Ice Ace Hub",
      ogDescription: "Calendrier complet des matchs de hockey pour toutes les grandes ligues.",
    },
    '/draft-rankings': {
      title: "Classement Du Repêchage LNH | Ice Ace Hub",
      description: "Meilleurs espoirs et classements du repêchage LNH. Découvrez les futures stars du hockey.",
      ogTitle: "Classement Repêchage LNH | Ice Ace Hub",
      ogDescription: "Classement complet du repêchage LNH et analyse des meilleurs espoirs.",
    },
    '/olympics-2026': {
      title: "Hockey Sur Glace Milano Cortina 2026 | Ice Ace Hub",
      description: "Programme complet du hockey sur glace aux Jeux olympiques d'hiver 2026, sites et diffusions. Tournois masculin et féminin du 6 au 22 février 2026.",
      ogTitle: "Olympiques 2026 Hockey | Ice Ace Hub",
      ogDescription: "Programme complet du hockey aux Olympiques Milano Cortina 2026 avec horaires, sites et informations de diffusion.",
    },
  },
};

const locales: Record<Language, string> = {
  en: 'en_US',
  sv: 'sv_SE',
  fi: 'fi_FI',
  ru: 'ru_RU',
  de: 'de_DE',
  cs: 'cs_CZ',
  fr: 'fr_FR',
};

const SEOProvider = ({ children }: { children: React.ReactNode }) => {
  const { language } = useLanguage();
  const location = useLocation();
  const baseUrl = "https://ice-ace-hub.lovable.app";

  useEffect(() => {
    const path = location.pathname;
    const seo = pageSEO[language]?.[path] || pageSEO['en']?.[path] || pageSEO[language]?.['/'] || pageSEO['en']['/'];
    const structuredData = structuredDataI18n[language];
    
    // Update document language
    document.documentElement.lang = language;
    
    // Update title
    document.title = seo.title;
    
    // Update meta tags
    updateMetaTag('description', seo.description);
    updateMetaTag('og:title', seo.ogTitle, 'property');
    updateMetaTag('og:description', seo.ogDescription, 'property');
    updateMetaTag('og:locale', locales[language], 'property');
    updateMetaTag('og:url', `${baseUrl}${path}`, 'property');
    updateMetaTag('twitter:title', seo.ogTitle, 'name');
    updateMetaTag('twitter:description', seo.ogDescription, 'name');
    
    // Update canonical
    updateCanonical(`${baseUrl}${path}`);
    
    // Update hreflang tags
    updateHreflangTags(baseUrl, path);
    
    // Update JSON-LD structured data
    updateStructuredData(baseUrl, path, language, seo, structuredData);
    
  }, [language, location.pathname, baseUrl]);

  return <>{children}</>;
};

function updateMetaTag(name: string, content: string, attribute: 'name' | 'property' = 'name') {
  let meta = document.querySelector(`meta[${attribute}="${name}"]`);
  if (meta) {
    meta.setAttribute('content', content);
  } else {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
}

function updateCanonical(href: string) {
  let link = document.querySelector('link[rel="canonical"]');
  if (link) {
    link.setAttribute('href', href);
  } else {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', href);
    document.head.appendChild(link);
  }
}

function updateHreflangTags(baseUrl: string, path: string) {
  // Remove existing hreflang links
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
  
  const languages: Language[] = ['en', 'sv', 'fi', 'ru', 'de', 'cs', 'fr'];
  
  // Add hreflang for each language
  languages.forEach(lang => {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = lang;
    link.href = `${baseUrl}${path}?lang=${lang}`;
    document.head.appendChild(link);
  });
  
  // Add x-default (points to URL without lang param)
  const xDefault = document.createElement('link');
  xDefault.rel = 'alternate';
  xDefault.hreflang = 'x-default';
  xDefault.href = `${baseUrl}${path}`;
  document.head.appendChild(xDefault);
}

function updateStructuredData(
  baseUrl: string, 
  path: string, 
  language: Language, 
  seo: SEOTranslations,
  i18n: StructuredDataTranslations
) {
  // Remove existing JSON-LD scripts (except static ones)
  document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]').forEach(el => el.remove());
  
  const languages: Language[] = ['en', 'sv', 'fi', 'ru', 'de', 'cs'];
  
  // Build breadcrumb based on path
  const breadcrumbItems = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": i18n.breadcrumbHome,
      "item": baseUrl
    }
  ];
  
  if (path === '/schedule') {
    breadcrumbItems.push({
      "@type": "ListItem",
      "position": 2,
      "name": i18n.breadcrumbSchedule,
      "item": `${baseUrl}/schedule`
    });
  } else if (path === '/draft-rankings') {
    breadcrumbItems.push({
      "@type": "ListItem",
      "position": 2,
      "name": i18n.breadcrumbDraft,
      "item": `${baseUrl}/draft-rankings`
    });
  } else if (path === '/olympics-2026') {
    breadcrumbItems.push({
      "@type": "ListItem",
      "position": 2,
      "name": i18n.breadcrumbOlympics,
      "item": `${baseUrl}/olympics-2026`
    });
  }
  
  // Get page-specific name
  let pageName = i18n.homePageName;
  let pageType = "WebPage";
  
  if (path === '/schedule') {
    pageName = i18n.schedulePageName;
    pageType = "CollectionPage";
  } else if (path === '/draft-rankings') {
    pageName = i18n.draftPageName;
    pageType = "CollectionPage";
  } else if (path === '/olympics-2026') {
    pageName = i18n.olympicsPageName;
    pageType = "CollectionPage";
  }
  
  // Create structured data object
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${baseUrl}/#website`,
        "url": baseUrl,
        "name": i18n.siteName,
        "description": i18n.siteDescription,
        "inLanguage": languages,
        "publisher": {
          "@id": `${baseUrl}/#organization`
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${baseUrl}/?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      },
      {
        "@type": "SportsOrganization",
        "@id": `${baseUrl}/#organization`,
        "name": i18n.siteName,
        "url": baseUrl,
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/og-image.png`,
          "width": 1200,
          "height": 630
        },
        "sameAs": [],
        "sport": "Ice Hockey"
      },
      {
        "@type": pageType,
        "@id": `${baseUrl}${path}/#webpage`,
        "url": `${baseUrl}${path}`,
        "name": pageName,
        "isPartOf": {
          "@id": `${baseUrl}/#website`
        },
        "about": {
          "@id": `${baseUrl}/#organization`
        },
        "description": seo.description,
        "inLanguage": language,
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbItems
        }
      }
    ]
  };
  
  // Add page-specific schema types
  if (path === '/schedule') {
    (structuredData["@graph"] as unknown[]).push({
      "@type": "SportsEvent",
      "@id": `${baseUrl}/schedule/#event`,
      "name": i18n.schedulePageName,
      "description": seo.description,
      "sport": "Ice Hockey",
      "url": `${baseUrl}/schedule`,
      "inLanguage": language
    });
  } else if (path === '/draft-rankings') {
    (structuredData["@graph"] as unknown[]).push({
      "@type": "ItemList",
      "@id": `${baseUrl}/draft-rankings/#itemlist`,
      "name": i18n.draftPageName,
      "description": seo.description,
      "itemListOrder": "https://schema.org/ItemListOrderDescending",
      "url": `${baseUrl}/draft-rankings`,
      "inLanguage": language
    });
  } else if (path === '/olympics-2026') {
    (structuredData["@graph"] as unknown[]).push({
      "@type": "SportsEvent",
      "@id": `${baseUrl}/olympics-2026/#event`,
      "name": i18n.olympicsPageName,
      "description": seo.description,
      "sport": "Ice Hockey",
      "url": `${baseUrl}/olympics-2026`,
      "inLanguage": language,
      "startDate": "2026-02-06",
      "endDate": "2026-02-22",
      "location": {
        "@type": "Place",
        "name": "Milano Cortina, Italy",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "IT"
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "International Olympic Committee"
      }
    });
  } else if (path === '/pricing') {
    (structuredData["@graph"] as unknown[]).push({
      "@type": "FAQPage",
      "@id": `${baseUrl}/pricing/#faq`,
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Can I cancel anytime?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! You can cancel your subscription at any time from your account settings. You'll keep access until the end of your billing period."
          }
        },
        {
          "@type": "Question",
          "name": "What happens after the free trial?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "After your trial ends, you'll be charged the monthly rate for your selected tier. You can upgrade, downgrade, or cancel before the trial ends."
          }
        },
        {
          "@type": "Question",
          "name": "What's the Elite access during trial?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Light and Pro subscribers get full Elite features (including betting odds) for the first 5 days, then revert to their tier-specific features for the remaining trial period."
          }
        }
      ]
    });
  }
  
  // Create and inject the script
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-dynamic', 'true');
  // SECURITY: This JSON.stringify output contains only static, developer-defined strings
  // from structuredDataI18n and pageSEO. No user input is included.
  script.textContent = JSON.stringify(structuredData);
  document.head.appendChild(script);
}

export default SEOProvider;
